import React, { useEffect, useState, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { Table, Typography, Spin, Modal, Alert, Input, Select, Space } from 'antd';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

const { Title, Paragraph } = Typography;
const { Search } = Input;
const { Option } = Select;

// Utility to safely render values (simple fallback)
const safe = (v) => (v === null || v === undefined ? '-' : v);

const formatDate = (d) => {
  if (!d) return '-';
  const date = new Date(d);
  if (isNaN(date.getTime())) return '-';
  const dd = String(date.getDate()).padStart(2, '0');
  const mm = String(date.getMonth() + 1).padStart(2, '0');
  const yyyy = date.getFullYear();
  return `${dd}-${mm}-${yyyy}`;
};

// extract month name (e.g. "NOVEMBER") from AvailabilityBatch like "23-NOVEMBER-2025" or a Date string
const extractMonthName = (s) => {
  if (!s) return null;
  // try pattern like 23-NOVEMBER-2025
  const m = s.match(/-(JANUARY|FEBRUARY|MARCH|APRIL|MAY|JUNE|JULY|AUGUST|SEPTEMBER|OCTOBER|NOVEMBER|DECEMBER)-/i);
  if (m) return m[1].toUpperCase();
  // try splitting by - and taking the middle part
  const parts = s.split('-').map(p => p.trim());
  if (parts.length >= 2) {
    const maybe = parts[1].toUpperCase();
    if (/^[A-Z]+$/.test(maybe)) return maybe;
  }
  // try parsing as date
  const date = new Date(s);
  if (!isNaN(date.getTime())) return date.toLocaleString('default', { month: 'long' }).toUpperCase();
  return null;
};

const MONTHS = [
  'JANUARY','FEBRUARY','MARCH','APRIL','MAY','JUNE','JULY','AUGUST','SEPTEMBER','OCTOBER','NOVEMBER','DECEMBER'
];

export default function UpcomingBatch() {
  const navigate = useNavigate();
  const location = useLocation();

  const token = localStorage.getItem('token');
  const email = localStorage.getItem('email');
  const role = localStorage.getItem('role');

  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [error, setError] = useState(null);

  // UI state: search + filters
  const [searchName, setSearchName] = useState('');
  const [searchEmail, setSearchEmail] = useState('');
  const [filterMonth, setFilterMonth] = useState(null); // e.g. 'NOVEMBER'
  const [filterBatchDay, setFilterBatchDay] = useState(null); // MORNING/EVENING
  const [filterLeadStage, setFilterLeadStage] = useState(null); // DEMO/NURTURING/FIRST INSTALLMENT

  useEffect(() => {
    const verifyUser = async () => {
      if (!token) {
        navigate('/login');
        return;
      }
      try {
        const res = await axios.post(
          `${process.env.REACT_APP_API_URL}/user/check-user`,
          { email, role, token }
        );
        if (!res.data.success) {
          const errMsg = res?.data?.message || 'Please Login Again.';
          toast.error(errMsg);
          localStorage.clear();
          navigate('/login');
        }
      } catch (error) {
        const msg = error?.response?.data?.message || error?.message || 'Please Login Again.';
        toast.error(msg);
        localStorage.clear();
        navigate('/login');
      }
    };
    verifyUser();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate, location.pathname, token, email, role]);

  const fetchRows = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await axios.get(`${process.env.REACT_APP_API_URL}/upcomingbatch/all-upcoming-batch-leads`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setRows(res.data?.data || []);
    } catch (err) {
      setError(err?.response?.data?.message || err.message || 'Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRows();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, location.key, navigate]);

  // derive available months from rows (for cases where values may differ)
  const availableMonths = useMemo(() => {
    const s = new Set();
    rows.forEach(r => {
      const m = extractMonthName(r.AvailabilityBatch);
      if (m) s.add(m);
    });
    // prefer standard MONTHS order but include only those present
    return MONTHS.filter(m => s.has(m));
  }, [rows]);

  // filtered leads applying search and filters
  const filteredLeads = useMemo(() => {
    return rows.filter(r => {
      // search name
      if (searchName && !`${r.FullName || ''}`.toLowerCase().includes(searchName.toLowerCase())) return false;
      // search email
      if (searchEmail && !`${r.Email || ''}`.toLowerCase().includes(searchEmail.toLowerCase())) return false;
      // filter month
      if (filterMonth) {
        const m = extractMonthName(r.AvailabilityBatch);
        if (!m || m.toUpperCase() !== filterMonth.toUpperCase()) return false;
      }
      // filter batch day
      if (filterBatchDay) {
        if (!r.BatchDay || r.BatchDay.toString().toUpperCase() !== filterBatchDay.toUpperCase()) return false;
      }
      // filter lead stage
      if (filterLeadStage) {
        if (!r.LeadStage || r.LeadStage.toString().toUpperCase() !== filterLeadStage.toUpperCase()) return false;
      }
      return true;
    });
  }, [rows, searchName, searchEmail, filterMonth, filterBatchDay, filterLeadStage]);

  // Action modal state + helpers (kept from your previous implementation)
  const [actionModalVisible, setActionModalVisible] = useState(false);
  const [actionRecord, setActionRecord] = useState(null);
  const [movingKeys, setMovingKeys] = useState(new Set());
  const setMoving = (key, on = true) => setMovingKeys(prev => {
    const next = new Set(prev);
    if (on) next.add(key); else next.delete(key);
    return next;
  });
  const openActionModal = (record) => { setActionRecord(record); setActionModalVisible(true); };

  const columns = [
    {
      title: <span className="text-[17px] font-bold text-white">Full Name</span>,
      dataIndex: 'FullName',
      key: 'FullName',
      width: 160,
      render: (t) => <span className="flex items-center ml-5 font-bold text-green-700 justify-left whitespace-nowrap">{safe(t)}</span>,
    },
    {
      title: <span className="text-[17px] font-bold text-white">Email</span>,
      dataIndex: 'Email',
      key: 'Email',
      width: 220,
      render: (t) => <span className="ml-10 font-bold whitespace-nowrap">{safe(t)}</span>,
    },
    {
      title: <span className="text-[17px] font-bold text-white">Phone Number</span>,
      dataIndex: 'PhoneNumber',
      key: 'PhoneNumber',
      width: 160,
      render: (t) => <span className="ml-10 whitespace-nowrap">{safe(t)}</span>,
    },
    {
      title: <span className="text-[17px] font-bold text-white">Lead Date</span>,
      dataIndex: 'LeadDate',
      key: 'LeadDate',
      width: 130,
      render: (d) => <span className="ml-5 font-bold text-red-700 whitespace-nowrap">{(d)}</span>,
    },
    {
      title: <span className="text-[17px] font-bold text-white">Lead Source</span>,
      dataIndex: 'LeadSource',
      key: 'LeadSource',
      width: 130,
      render: (t) => <span className="ml-4 font-bold text-cyan-800 whitespace-nowrap">{safe(t)}</span>,
    },
     {
      title: <span className="text-[17px] font-bold text-white">Enrollment Type</span>,
      dataIndex: 'EnrollmentType',
      key: 'EnrollmentType',
      width: 260,
      render: (t) => <span className="ml-4 font-bold text-red-800 whitespace-nowrap">{safe(t)}</span>,
    },
    {
      title: <span className="text-[17px] font-bold text-white">Lead Category</span>,
      dataIndex: 'LeadCategory',
      key: 'LeadCategory',
      width: 160,
      render: (t) => <span className="ml-4 font-bold text-cyan-800 whitespace-nowrap">{safe(t)}</span>,
    },
    {
      title: <span className="text-[17px] font-bold text-white">Availability Batch</span>,
      dataIndex: 'AvailabilityBatch',
      key: 'AvailabilityBatch',
      width: 180,
      render: (t) => <span className="ml-5 font-bold text-green-700 whitespace-nowrap">{safe(t)}</span>,
    },
    {
      title: <span className="text-[17px] font-bold text-white">Batch Day</span>,
      dataIndex: 'BatchDay',
      key: 'BatchDay',
      width: 120,
      render: (t) => <span className="ml-5 font-bold whitespace-nowrap">{safe(t)}</span>,
    },
    {
      title: <span className="text-[17px] font-bold text-white">Lead Stage</span>,
      dataIndex: 'LeadStage',
      key: 'LeadStage',
      width: 130,
      render: (t) => <span className="flex justify-center font-bold text-pink-700 whitespace-nowrap">{safe(t)}</span>,
    },
    {
      title: <span className="text-[17px] font-bold text-white">Comments</span>,
      dataIndex: 'Comments',
      key: 'Comments',
      width: 350,
      render: (t) => <span className="ml-8 whitespace-normal">{safe(t)}</span>,
    },
    {
      title: (
        <span className="font-bold text-[17px] text-white whitespace-nowrap">Action</span>
      ),
      key: 'action',
      fixed: 'right',
      width: 140,
      align: 'center',
      render: (_, record) => (
        <div className="flex items-center justify-center gap-2">
          <button
            onClick={() => openActionModal(record)}
            className={`px-4 py-2 rounded-md text-white font-semibold transition-colors duration-200 whitespace-nowrap bg-gradient-to-r from-blue-600 to-blue-500 hover:from-red-700 hover:to-rose-600 shadow`}
            type="button"
          >
            Actions
          </button>
        </div>
      ),
    }
  ];

  // moveToDemo + deleteLead kept as-is (from your previous code)
  const API = process.env.REACT_APP_API_URL;

  const moveToDemo = async (record) => {
    if (!record) return;
    const key = `move-pending-installment-${record.id ?? record.Email}`;
    setMoving(key, true);

    try {
      const payload = { Email: record.Email };

      const res = await axios.post(
        `${API}/upcomingbatch/move-to-demo-stage`,
        payload,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      toast.success(res?.data?.message || 'Lead moved to Demo successfully');

      setRows(prev => prev.filter(r => !(r.id === record.id || r.Email === record.Email)));

      setActionModalVisible(false);
      setActionRecord(null);
    } catch (err) {
      const msg = err?.response?.data?.message || err?.message || 'Failed to move lead to demo';
      toast.error(msg);
    } finally {
      setMoving(key, false);
    }
  };

  const deleteLead = async (record) => {
    if (!record) return;
    const key = `delete-lead-${record.id ?? record.Email}`;
    setMoving(key, true);

    try {
      const payload = { Email: record.Email };
      const res = await axios.delete(
        `${API}/upcomingbatch/delete-upcoming-batch-lead`,
        { headers: { Authorization: `Bearer ${token}` },
          data: { Email: payload.Email },
        }
      );

      toast.success(res?.data?.message || 'Lead deleted successfully');

      setRows(prev => prev.filter(r => !(r.id === record.id || r.Email === record.Email)));

      setActionModalVisible(false);
      setActionRecord(null);
    } catch (err) {
      const msg = err?.response?.data?.message || err?.message || 'Failed to delete lead';
      toast.error(msg);
    } finally {
      setMoving(key, false);
    }
  };

  return (
    <div className="p-6">
      <ToastContainer
        position="bottom-left"
        autoClose={5000}
        hideProgressBar={true}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="dark"
        className="!mb-6 !ml-4"
        toastClassName="!inline-flex !items-center !whitespace-nowrap !bg-black !text-white !px-4 !py-2 !rounded-lg !w-auto !min-w-0 !shadow-lg"
      />

      <Title level={3} className="!text-3xl !font-bold text-gray-800 tracking-tight mb-3 border-b border-gray-200 pb-2">
        Upcoming Batches
      </Title>

      {error && <Alert message="Error" description={error} type="error" showIcon style={{ marginBottom: 12 }} />}

      <Paragraph className="mb-6 text-base leading-relaxed text-gray-700">
        Upcoming batch leads and enrolled students are listed below. Review their details, batch preferences, and lead stages to plan schedules, organize sessions, and ensure smooth onboarding for each participant.
      </Paragraph>

      {/* Controls: search and filters */}
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <Search
          placeholder="Search Full Name"
          allowClear
          onSearch={(v) => setSearchName(v)}
          onChange={(e) => setSearchName(e.target.value)}
          style={{ width: 260 }}
        />

        <Search
          placeholder="Search Email"
          allowClear
          onSearch={(v) => setSearchEmail(v)}
          onChange={(e) => setSearchEmail(e.target.value)}
          style={{ width: 300 }}
        />

        <Select
          allowClear
          placeholder="Filter: Month"
          value={filterMonth}
          onChange={(v) => setFilterMonth(v)}
          style={{ width: 180 }}
        >
          {/* Show available months detected in data; fallback to full list */}
          {(availableMonths.length ? availableMonths : MONTHS).map(m => (
            <Option key={m} value={m}>{m}</Option>
          ))}
        </Select>

        <Select
          allowClear
          placeholder="Filter: Batch Day"
          value={filterBatchDay}
          onChange={(v) => setFilterBatchDay(v)}
          style={{ width: 160 }}
        >
          <Option value="MORNING">MORNING</Option>
          <Option value="EVENING">EVENING</Option>
        </Select>

        <Select
          allowClear
          placeholder="Filter: Lead Stage"
          value={filterLeadStage}
          onChange={(v) => setFilterLeadStage(v)}
          style={{ width: 200 }}
        >
          <Option value="DEMO">DEMO</Option>
          <Option value="NURTURING">NURTURING</Option>
          <Option value="FIRST INSTALLMENT">FIRST INSTALLMENT</Option>
        </Select>

        <Space>
          <button
            onClick={() => { setSearchName(''); setSearchEmail(''); setFilterMonth(null); setFilterBatchDay(null); setFilterLeadStage(null); }}
            className="px-4 py-2 text-sm bg-gray-200 rounded-md hover:bg-gray-300"
            type="button"
          >
            Reset
          </button>
        </Space>
      </div>

      <Spin spinning={loading} tip="Loading...">
        <Table
          dataSource={filteredLeads}
          columns={columns}
          loading={loading}
          pagination={{ pageSize: 10, total: filteredLeads.length }}
          scroll={{ x: 2300 }}
          className="font-semibold text-center border border-gray-200 rounded-lg shadow-sm gray-header-table-dates"
          rowKey={(r) => r.id || r.Email}
        />
      </Spin>

      {/* Action Modal */}
      <Modal
        title={<div className="flex items-center w-full justify-left"><span className="text-xl font-bold text-gray-900">CHOOSE ACTION FOR LEAD</span></div>}
        open={actionModalVisible}
        onCancel={() => setActionModalVisible(false)}
        footer={null}
        centered
        width={480}
        bodyStyle={{ padding: 24 }}
      >
        <div className="space-y-4">
          <div className="text-sm text-gray-700">
            Performing action for: <strong>{actionRecord?.FullName ?? actionRecord?.Email ?? '-'}</strong>
            {actionRecord?.PhoneNumber ? <span className="ml-2 text-xs font-semibold text-gray-700">({actionRecord.PhoneNumber})</span> : null}
          </div>

          <div className="flex flex-col gap-3">
            <button
              onClick={() => moveToDemo(actionRecord)}
              disabled={!actionRecord || movingKeys.has(`move-pending-installment-${actionRecord?.id ?? actionRecord?.Email}`)}
              className={`w-full px-5 py-3 rounded-xl text-white font-semibold shadow-md transition-transform active:scale-[0.99] ${movingKeys.has(`move-pending-installment-${actionRecord?.id ?? actionRecord?.Email}`) ? 'bg-blue-400 cursor-wait' : 'bg-blue-600 hover:bg-blue-700'}`}
              type="button"
            >
              {movingKeys.has(`move-pending-installment-${actionRecord?.id ?? actionRecord?.Email}`) ? <span className="inline-flex items-center gap-2"><Spin size="small" />Moving...</span> : 'Move To Demo'}
            </button>

            <button
              onClick={() => deleteLead(actionRecord)}
              disabled={!actionRecord || movingKeys.has(`delete-lead-${actionRecord?.id ?? actionRecord?.Email}`)}
              className={`w-full px-5 py-3 rounded-xl text-white font-semibold shadow-md transition-transform active:scale-[0.99] ${movingKeys.has(`delete-lead-${actionRecord?.id ?? actionRecord?.Email}`) ? 'bg-indigo-400 cursor-wait' : 'bg-red-700 hover:bg-red-800'}`}
              type="button"
            >
              {movingKeys.has(`delete-lead-${actionRecord?.id ?? actionRecord?.Email}`) ? <span className="inline-flex items-center gap-2"><Spin size="small" />Deleting...</span> : 'Delete The Lead'}
            </button>
          </div>
        </div>
      </Modal>

    </div>
  );
}
